import { useGlobalToasts } from '@src/hooks/Toast/useGlobalToasts';
import { noop } from 'lodash-es';
import { useCallback, useState } from 'react';
import { Box, VStack } from '../../Core';
import { DrawerContent } from '../../Drawer';
import { TextArea } from '../../Form/TextArea';
import { NotificationVariants } from '../../Notification';
import type { EntityAdminRecord } from '../types';
import { createSaveContext } from '../utils';
import { EntityAdminDrawerFooter } from './EntityAdminDrawerFooter';
import type { BaseEntityAdminDrawerProps } from './InputsAndDropdownsDrawer/InputsAndDropdownsDrawer';

export function ModifyJsonDrawer<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  onSaveEntities,
  suppressEditingFooterActions,
}: Pick<
  BaseEntityAdminDrawerProps<TRecord, TDrawerRecord>,
  'selectedEntities' | 'onSaveEntities' | 'suppressEditingFooterActions'
>) {
  const { add: addToast } = useGlobalToasts();

  const [modifiedEntityString, setModifiedEntityString] = useState<string>(JSON.stringify(selectedEntities, null, 4));

  const handleOnSave = useCallback(async () => {
    try {
      const modifiedEntity = JSON.parse(modifiedEntityString) as TDrawerRecord;
      // Pass EntityAdminClass[] directly (ModifyJsonDrawer is always editing)
      const entityClasses = selectedEntities;
      // ModifyJsonDrawer doesn't support adding child entities, so it's always editing and never adding child entities
      const context = createSaveContext({ isEditing: true, addingChildEntity: false, selectedEntities });
      await onSaveEntities(entityClasses, modifiedEntity, context);
    } catch (error) {
      if (error instanceof SyntaxError) {
        addToast({
          text: 'Failed to save entity. Please check the JSON format.',
          variant: NotificationVariants.Negative,
        });
      } else {
        addToast({
          text: `Failed to save entity: ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: NotificationVariants.Negative,
        });
      }
      // Re-throw to prevent the drawer from closing on error
      throw error;
    }
  }, [addToast, modifiedEntityString, onSaveEntities, selectedEntities]);

  return (
    <VStack data-testid="entity-admin-page-json-drawer" h="100%">
      <DrawerContent overflow="overlay" w="100%">
        <Box>
          <TextArea value={modifiedEntityString} rows={15} onChange={e => setModifiedEntityString(e.target.value)} />
        </Box>
      </DrawerContent>
      {!(suppressEditingFooterActions && selectedEntities.length > 0) && (
        <EntityAdminDrawerFooter handleOnSave={handleOnSave} someError={false} runValidation={noop} />
      )}
    </VStack>
  );
}
